/**
 * Which areas of RT Hub are split by market (Australia / USA), and which are one shared copy.
 *
 * Josh, 13 Oct 2026: "everything separate except payroll and RT hub" — and "rt hub is the
 * screenshots parts and timesheets". So every team's area is its own per market, and two things are
 * one copy for both: Payroll, and RT Hub (the workforce area: roster, screenshots, timesheets).
 *
 * ⛔ TWO LISTS, ON PURPOSE.
 *   - MARKET_AREA_RULING is the decision: what each area is MEANT to be.
 *   - MARKET_AREAS_BUILT is the truth today: which separate areas actually keep their records apart.
 * The screen labels an area with its market only when it is in BOTH. An area ruled separate but not
 * built yet says so ("Not split yet") rather than wearing a "USA" label over Australian records —
 * the label is a promise about what is behind the door. Each PR that separates an area adds its key
 * to MARKET_AREAS_BUILT in the same change that fences its records, and not before.
 *
 * ONE definition, read by the web (labels, the switch, which calls name a market) so the hub page,
 * every sidebar and the request header can never disagree.
 */
/** The hub tiles, by the key PlatformHome uses. */
export const MARKET_AREA_KEYS = [
    'sales',
    'partnerships',
    'recruiting',
    'assessments',
    'rt-screen',
    'onboarding',
    'tickets',
    'whatsapp',
    'messages',
    'wiki',
    'recognition',
    'phone-numbers',
    'rt-tv',
    'marketing-bot',
    'automations',
    'hub',
    'payroll',
    'my-hours',
    'reimbursements',
    'invoices',
    'payslips',
    'team',
];
/**
 * The decision. Payroll and RT Hub are Josh's words. My Hours, Reimbursements and Team & Access are
 * shared because they ARE those two seen from one person's side — your own timesheet, your own
 * claim into payroll, and the one list of people both markets draw on — shown to Josh as shared in
 * the 13 Oct preview, which he approved ("yes looks right"). My Payslips (23 Oct 2026) is the same:
 * your own payslips and payroll disputes are Payroll seen from your side. My Invoices (3 Nov 2026) too:
 * your own salary invoice into payroll.
 */
export const MARKET_AREA_RULING = {
    sales: 'SEPARATE',
    partnerships: 'SEPARATE',
    recruiting: 'SEPARATE',
    assessments: 'SEPARATE',
    // RT Screen (28 Oct 2026): screening is recruiting, so separate under the 13 Oct ruling. BUILT since its
    // tables arrived fenced (#3801): every row takes its market from its client or candidate.
    'rt-screen': 'SEPARATE',
    onboarding: 'SEPARATE',
    tickets: 'SEPARATE',
    whatsapp: 'SEPARATE',
    messages: 'SEPARATE',
    wiki: 'SEPARATE',
    recognition: 'SEPARATE',
    'phone-numbers': 'SEPARATE',
    'rt-tv': 'SEPARATE',
    'marketing-bot': 'SEPARATE',
    // Automation (workflows + email templates): ruled separate under Josh's "everything separate except
    // payroll and RT Hub" (13 Oct 2026) — NOT built: its records are one set for now ("Not split yet").
    automations: 'SEPARATE',
    hub: 'SHARED',
    payroll: 'SHARED',
    'my-hours': 'SHARED',
    reimbursements: 'SHARED',
    invoices: 'SHARED',
    payslips: 'SHARED',
    team: 'SHARED',
};
/** ⛔ The separate areas whose records really are kept apart today. Add a key ONLY in the change
 *  that fences that area's records. */
export const MARKET_AREAS_BUILT = ['sales', 'partnerships', 'onboarding', 'recruiting', 'assessments', 'tickets', 'wiki', 'recognition', 'messages', 'rt-screen'];
export function marketAreaState(key) {
    if (MARKET_AREA_RULING[key] === 'SHARED')
        return 'SHARED';
    return MARKET_AREAS_BUILT.includes(key) ? 'SEPARATE' : 'NOT_SPLIT_YET';
}
// Where each area lives in the web app. Longest prefix wins, so /hub/payroll is Payroll rather than
// RT Hub and /sales/marketing-bot is the Marketing Bot rather than the Sales CRM.
const AREA_PATHS = [
    ['/sales/marketing-bot', 'marketing-bot'],
    ['/sales', 'sales'],
    ['/partnerships', 'partnerships'],
    ['/recruiting', 'recruiting'],
    ['/assessments', 'assessments'],
    ['/rt-screen', 'rt-screen'],
    ['/onboarding', 'onboarding'],
    ['/tickets', 'tickets'],
    ['/whatsapp', 'whatsapp'],
    ['/messages', 'messages'],
    ['/wiki', 'wiki'],
    ['/recognition', 'recognition'],
    ['/hub/phone-numbers', 'phone-numbers'],
    ['/hub/payroll', 'payroll'],
    ['/hub', 'hub'],
    ['/rt-tv', 'rt-tv'],
    ['/automations', 'automations'],
    ['/my-hours', 'my-hours'],
    ['/reimbursements', 'reimbursements'],
    ['/my-invoices', 'invoices'],
    ['/payslips', 'payslips'],
    ['/payroll-disputes', 'payslips'],
    ['/team', 'team'],
];
/** The area a page belongs to, or null for a page that is in none (the hub home, settings, login). */
export function marketAreaOfPath(pathname) {
    const path = pathname.split(/[?#]/)[0] ?? '';
    let best = null;
    for (const entry of AREA_PATHS) {
        const [prefix] = entry;
        if ((path === prefix || path.startsWith(`${prefix}/`)) && (!best || prefix.length > best[0].length))
            best = entry;
    }
    return best ? best[1] : null;
}
/** The front page of an area — where the Market switch lands you after a change. */
export function marketAreaHome(key) {
    return AREA_PATHS.find(([, k]) => k === key)?.[0] ?? '/';
}
/**
 * Should a request made from this page say which market it is for?
 *
 * Only from a page inside an area that is separate AND built. Everywhere else the request names no
 * market, and the server fences it to ALL the person's markets:
 *   - a shared area (RT Hub, Payroll) is one copy, so someone in both must see both there;
 *   - an area not split yet still holds every market's records in one list, and narrowing its
 *     requests to one market would hide the leads behind half of them.
 * Access is unaffected either way — the server only ever shows a person their own markets.
 */
export function pageNamesItsMarket(pathname) {
    const area = marketAreaOfPath(pathname);
    return area !== null && marketAreaState(area) === 'SEPARATE';
}
//# sourceMappingURL=marketAreas.js.map